// ============================================================
// MongoDBIndexing.jsx
//
// A branded guide to Indexing strategies, Compound Indexes,
// and Query Optimization in MongoDB.
//
// Brand palette:
//   Background  → #001E2B  (MongoDB Deep Navy)
//   Primary     → #00E0FF  (Electric Cyan / High Speed)
//   Accent      → #00ED64  (MongoDB Green)
//   Text        → #FFFFFF  (White)
//   Code        → #010409  (GitHub Dark)
// ============================================================

import React, { useState } from "react";
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Divider,
    Paper,
    Stack,
    IconButton,
    Avatar,
    Button,
    Alert
} from "@mui/material";

/* MUI Icons */
import SpeedIcon from "@mui/icons-material/Speed";
import BoltIcon from "@mui/icons-material/Bolt";
import SearchIcon from "@mui/icons-material/Search";
import SchoolIcon from "@mui/icons-material/School";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DoneIcon from "@mui/icons-material/Done";
import StorageIcon from "@mui/icons-material/Storage";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import AssessmentIcon from "@mui/icons-material/Assessment";

/* ── Brand tokens ────────────────────────────────────────── */
const BRAND = {
    bg: "#001E2B",
    surface: "rgba(255,255,255,0.04)",
    border: "rgba(0, 224, 255, 0.2)", // Cyan Border
    cyan: "#00E0FF",
    green: "#00ED64",
    white: "#FFFFFF",
    muted: "rgba(255,255,255,0.6)",
    codeBg: "#010409",
};

/* ── Sub-Components ──────────────────────────────────────── */

function CodeTerminal({ code, label, color = BRAND.cyan }) {
    const [copied, setCopied] = useState(false);
    const handleCopy = () => {
        navigator.clipboard.writeText(code);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <Box sx={{ background: BRAND.codeBg, borderRadius: 3, border: `1px solid ${BRAND.border}`, overflow: "hidden", mb: 4 }}>
            <Box sx={{ px: 2, py: 1, display: "flex", justifyContent: "space-between", background: "rgba(255,255,255,0.02)" }}>
                <Typography variant="caption" sx={{ color: color, fontWeight: 700, fontFamily: "monospace" }}>{label}</Typography>
                <IconButton size="small" onClick={handleCopy} sx={{ color: BRAND.muted }}>
                    {copied ? <DoneIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                </IconButton>
            </Box>
            <Box sx={{ p: 2.5, overflowX: "auto" }}>
                <Typography component="pre" sx={{ fontFamily: "'Fira Code', monospace", fontSize: 13, color: "#BAE6FD", lineHeight: 1.5 }}>
                    {code}
                </Typography>
            </Box>
        </Box>
    );
}

/* ── Main Component ──────────────────────────────────────── */

export default function MongoDBIndexing() {
    return (
        <Box sx={{ minHeight: "100vh", background: BRAND.bg, py: { xs: 6, md: 10 }, color: BRAND.white }}>
            <Container maxWidth="lg">

                {/* 1. HERO HEADER */}
                <Box sx={{ mb: 10, textAlign: { xs: "center", md: "left" } }}>
                    <Chip
                        icon={<SchoolIcon style={{ color: BRAND.cyan }} />}
                        label="Performance Engineering · Module 11"
                        sx={{ bgcolor: "rgba(0,224,255,0.1)", color: BRAND.cyan, border: `1px solid ${BRAND.border}`, mb: 2 }}
                    />
                    <Typography variant="h2" sx={{ fontWeight: 900, mb: 2, letterSpacing: "-1.5px" }}>
                        Query <span style={{ color: BRAND.cyan }}>Optimization</span>
                    </Typography>
                    <Typography sx={{ color: BRAND.muted, fontSize: 18, maxWidth: 800, lineHeight: 1.7 }}>
                        Don't let your database crawl. <strong>Indexing</strong> creates specialized data structures
                        that allow MongoDB to find documents without scanning every single record in a collection.
                    </Typography>
                </Box>

                {/* 2. THE COLLSCAN PROBLEM */}
                <Grid container spacing={6} alignItems="center" sx={{ mb: 10 }}>
                    <Grid item xs={12} md={5}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
                            <SearchIcon sx={{ color: BRAND.cyan, fontSize: 32 }} />
                            <Typography variant="h5" sx={{ fontWeight: 800 }}>COLLSCAN vs. IXSCAN</Typography>
                        </Box>
                        <Typography sx={{ color: BRAND.muted, mb: 3, lineHeight: 1.8 }}>
                            Without an index, MongoDB performs a <strong>Collection Scan (COLLSCAN)</strong>.
                            It’s like reading an entire book to find one specific word. With an index
                            (<strong>IXSCAN</strong>), it uses an alphabetized "Index" at the back of the book to jump straight to the page.
                        </Typography>
                    </Grid>
                    <Grid item xs={12} md={7}>

                    </Grid>
                </Grid>

                <Divider sx={{ borderColor: "rgba(255,255,255,0.05)", mb: 10 }} />

                {/* 3. COMPOUND INDEXES */}
                <Box sx={{ mb: 10 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 4 }}>
                        <BoltIcon sx={{ color: BRAND.cyan, fontSize: 32 }} />
                        <Typography variant="h5" sx={{ fontWeight: 800 }}>Compound Indexes</Typography>
                    </Box>
                    <Typography sx={{ color: BRAND.muted, mb: 4, lineHeight: 1.8 }}>
                        When queries filter on multiple fields (e.g., <code>status</code> AND <code>date</code>),
                        a <strong>Compound Index</strong> is required for maximum efficiency. The <strong>order of fields</strong>
                        matters: follow the <strong>ESR Rule</strong> (Equality, Sort, Range).
                    </Typography>



                    <Grid container spacing={4}>
                        <Grid item xs={12} md={7}>
                            <CodeTerminal
                                label="mongosh: Creating Indexes"
                                code={`// Single Field Index\ndb.users.createIndex({ email: 1 });\n\n// Compound Index (Order matters!)\ndb.users.createIndex({ status: 1, lastLogin: -1 });\n\n// List all indexes\ndb.users.getIndexes();`}
                            />
                        </Grid>
                        <Grid item xs={12} md={5}>
                            <Stack spacing={2}>
                                <Paper sx={{ p: 2, bgcolor: BRAND.surface, borderRadius: 3, border: `1px solid ${BRAND.border}` }}>
                                    <Typography variant="subtitle2" sx={{ color: BRAND.cyan }}>Ascending (1)</Typography>
                                    <Typography variant="body2" sx={{ color: BRAND.muted }}>Sorts values from smallest to largest.</Typography>
                                </Paper>
                                <Paper sx={{ p: 2, bgcolor: BRAND.surface, borderRadius: 3, border: `1px solid ${BRAND.border}` }}>
                                    <Typography variant="subtitle2" sx={{ color: BRAND.cyan }}>Descending (-1)</Typography>
                                    <Typography variant="body2" sx={{ color: BRAND.muted }}>Sorts values from largest to smallest.</Typography>
                                </Paper>
                            </Stack>
                        </Grid>
                    </Grid>
                </Box>

                {/* 4. PERFORMANCE PROFILING */}
                <Box sx={{ mb: 10 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 4 }}>
                        <AssessmentIcon sx={{ color: BRAND.cyan, fontSize: 32 }} />
                        <Typography variant="h5" sx={{ fontWeight: 800 }}>Explain Plans</Typography>
                    </Box>
                    <Typography sx={{ color: BRAND.muted, mb: 4 }}>
                        Always verify your indexes using the <code>.explain()</code> method. It reveals how many
                        documents were examined versus how many were returned.
                    </Typography>

                    <CodeTerminal
                        label="mongosh: Performance Check"
                        code={`db.users.find({ email: "dev@example.com" }).explain("executionStats");\n\n// Look for: "stage": "IXSCAN"\n// Red Flag: "stage": "COLLSCAN" (Missing Index!)`}
                    />


                </Box>

                {/* 5. THE INDEX PENALTY */}
                <Box sx={{ mb: 10 }}>
                    <Alert severity="warning" sx={{ bgcolor: "rgba(0, 224, 255, 0.05)", color: BRAND.white, border: `1px solid ${BRAND.border}` }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
                            <QueryStatsIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                            The Write Penalty
                        </Typography>
                        <Typography variant="body2" sx={{ color: BRAND.muted }}>
                            Indexes speed up <strong>Reads</strong> but slow down <strong>Writes</strong> (Inserts/Updates).
                            Every time you add a document, MongoDB must also update all related indexes.
                            <strong> Never index fields you don't query!</strong>
                        </Typography>
                    </Alert>
                </Box>

                {/* FOOTER CTA */}
                <Box sx={{
                    p: 8, borderRadius: 8, textAlign: "center",
                    background: `linear-gradient(45deg, ${BRAND.bg}, #003B4A)`,
                    border: `1px solid ${BRAND.border}`,
                    boxShadow: `0 20px 40px rgba(0,0,0,0.5)`
                }}>
                    <SpeedIcon style={{ fontSize: 40, color: BRAND.cyan, marginBottom: 16 }} />
                    <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>Optimized for Speed?</Typography>
                    <Typography sx={{ color: BRAND.muted, mb: 4 }}>
                        Mastering indexes is what separates junior coders from senior database architects.
                        Next, let's explore Advanced Querying techniques.
                    </Typography>
                    <Stack direction="row" spacing={2} justifyContent="center">
                        <Button variant="contained" sx={{ bgcolor: BRAND.cyan, color: BRAND.bg, fontWeight: 900 }}>Benchmark Queries</Button>
                        <Button variant="outlined" sx={{ color: BRAND.white, borderColor: BRAND.muted }}>Index Limitations</Button>
                    </Stack>
                </Box>

            </Container>
        </Box>
    );
}
